import { useState } from 'react';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import { fontFamilyMono } from '@/theme/tokens';

/**
 * `secret`/`signingSecret` here are a declared, sanctioned exception (D-N) in
 * `scripts/check-no-credential-values.mjs`'s `SANCTIONED_SECRET_BEARING_TYPES`
 * registry — pinned to this exact type name, this exact file, and these two
 * property names; the guard fails if a third secret-shaped property is added
 * here, or if this type moves.
 */
interface CredentialSecretRevealPanelProps {
  open: boolean;
  title: string;
  /** Present only while the panel is open — sourced from the caller's own local component state (never storage). */
  secret: string | undefined;
  signingSecret: string | undefined;
  /** Discards the material: the caller must clear its own state here, never keep it around after this fires. */
  onClose: () => void;
}

/**
 * The one-time secret display required by D-N: rendered immediately after
 * `POST /v1/admin/api-credentials` or `POST .../rotate`. `secret` and
 * `signingSecret` live only in the caller's component state for the
 * lifetime of this panel — never written to `localStorage`,
 * `sessionStorage`, a cookie, a URL, or any log/telemetry call. Closing is
 * only possible through the explicit confirmation flow below; backdrop
 * click and Escape are disabled so a stray click never silently discards an
 * uncopied secret.
 */
export function CredentialSecretRevealPanel({ open, title, secret, signingSecret, onClose }: CredentialSecretRevealPanelProps) {
  const translate = useTranslate();
  const notify = useNotify();
  const [confirmingClose, setConfirmingClose] = useState(false);

  async function copy(value: string) {
    try {
      await navigator.clipboard.writeText(value);
      notify(translate('pay.credentials.secretPanel.copied'), { type: 'info' });
    } catch {
      // Clipboard access can be denied by the browser sandbox; the value remains selectable in the field.
    }
  }

  function requestClose() {
    setConfirmingClose(false);
    onClose();
  }

  if (!open || !secret || !signingSecret) return null;

  const monoFieldSx = { '& .MuiInputBase-input': { fontFamily: fontFamilyMono, fontSize: '0.8rem' } };

  return (
    <Dialog open={open} onClose={() => {}} disableEscapeKeyDown maxWidth="sm" fullWidth>
      {confirmingClose ? (
        <>
          <DialogTitle>{translate('pay.credentials.secretPanel.closeConfirmTitle')}</DialogTitle>
          <DialogContent>
            <Typography variant="body2">{translate('pay.credentials.secretPanel.closeConfirmBody')}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmingClose(false)}>{translate('pay.credentials.secretPanel.closeConfirmCancel')}</Button>
            <Button variant="contained" color="error" onClick={requestClose}>
              {translate('pay.credentials.secretPanel.closeConfirmConfirm')}
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle>{title}</DialogTitle>
          <DialogContent>
            <Stack spacing={2.5} sx={{ pt: 1 }}>
              <Alert severity="warning">{translate('pay.credentials.secretPanel.warning')}</Alert>
              <TextField
                size="small"
                label={translate('pay.credentials.secretPanel.fieldSecret')}
                value={secret}
                fullWidth
                sx={monoFieldSx}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => copy(secret)} aria-label={translate('pay.credentials.secretPanel.copy')}>
                        <ContentCopyOutlinedIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                size="small"
                label={translate('pay.credentials.secretPanel.fieldSigningSecret')}
                value={signingSecret}
                fullWidth
                sx={monoFieldSx}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => copy(signingSecret)}
                        aria-label={translate('pay.credentials.secretPanel.copy')}
                      >
                        <ContentCopyOutlinedIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button variant="contained" onClick={() => setConfirmingClose(true)}>
              {translate('pay.credentials.secretPanel.close')}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
